import {view, tokens} from '@sail/ui';
import {AnimatePresence, motion, tokens as motionTokens} from '@sail/motion';
import {Text} from 'src/v2/components/common/Text';
import {RadioOption} from 'src/v2/components/anyCrypto/refund/RadioOption';
import {FormTextField} from 'src/v2/components/anyCrypto/FormTextField';
import {getNetworkName} from 'src/components/Wallet/constants';
import type {RefundMethod} from 'src/v2/state/types';
import type {OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum} from '@stripe-internal/data-v1/schema/types';
import {message, useIntl} from '@sail/intl';

const messages = {
  refundMethodLabel: message({
    id: 'crypto.payins.v2.refund.refund_method_label',
    description: 'Label for refund method selector',
    defaultMessage: 'Preferred refund method',
  }),

  returnToSender: message({
    id: 'crypto.payins.v2.refund.return_to_sender',
    description: 'Option to return refund to sender address',
    defaultMessage: 'Return to sender',
  }),

  setNewRefundAddress: message({
    id: 'crypto.payins.v2.refund.set_new_refund_address',
    description: 'Option to set a new refund address',
    defaultMessage: 'Set a new refund address',
  }),

  refundAddressLabel: message({
    id: 'crypto.payins.v2.refund.refund_address_label',
    description: 'Label for refund wallet address input',
    defaultMessage: 'Refund wallet address',
  }),

  refundAddressPlaceholder: message({
    id: 'crypto.payins.v2.refund.refund_address_placeholder',
    description: 'Placeholder for refund wallet address input',
    defaultMessage: 'Enter your refund address',
  }),

  refundNetworkLabel: message<{
    networkName: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.refund.refund_network_label',
    description: 'Label showing which network the refund address is for',
    defaultMessage: 'Refund network: {networkName}',
  }),
};

type PreferredRefundMethodSectionProps = {
  refundMethod: RefundMethod;
  onRefundMethodChange: (method: RefundMethod) => void;
  customRefundAddress: string;
  onRefundAddressChange: (value: string) => void;
  selectedNetwork: OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum | null;
  addressError?: string;
};

export const PreferredRefundMethodSection = ({
  refundMethod,
  onRefundMethodChange,
  customRefundAddress,
  onRefundAddressChange,
  selectedNetwork,
  addressError,
}: PreferredRefundMethodSectionProps) => {
  const intl = useIntl();
  const networkName = selectedNetwork ? getNetworkName(selectedNetwork) : '';

  return (
    <view.div css={{stack: 'y', gap: 'space.150'}}>
      <Text
        type="captionEmphasized"
        css={{color: tokens.color.neutral[900].toString(), fontSize: '13px'}}
      >
        {intl.formatMessage(messages.refundMethodLabel)}
      </Text>
      <view.div
        css={{
          borderWidth: 'small',
          borderStyle: 'solid',
          borderColor: tokens.color.neutral[100].toString(),
          borderRadius: 'medium',
          overflow: 'hidden',
        }}
      >
        <RadioOption
          selected={refundMethod === 'sendToPaymentAddress'}
          label={intl.formatMessage(messages.returnToSender)}
          onSelect={() => onRefundMethodChange('sendToPaymentAddress')}
          name="refundMethod"
          value="sendToPaymentAddress"
        />
        <view.div
          css={{
            height: 'space.1',
            backgroundColor: tokens.color.neutral[100].toString(),
          }}
        />
        <RadioOption
          selected={refundMethod === 'setNewRefundAddress'}
          label={intl.formatMessage(messages.setNewRefundAddress)}
          onSelect={() => onRefundMethodChange('setNewRefundAddress')}
          name="refundMethod"
          value="setNewRefundAddress"
        />
        <AnimatePresence initial={false}>
          {refundMethod === 'setNewRefundAddress' && selectedNetwork ? (
            <view.div
              key="refund-address-input"
              uses={[
                motion({
                  initial: {height: 0, opacity: 0},
                  animate: {height: 'auto', opacity: 1},
                  exit: {height: 0, opacity: 0},
                  transition: {duration: 0.2, ease: motionTokens.easing.smooth},
                }),
              ]}
              css={{overflow: 'hidden'}}
            >
              <view.div
                css={{
                  stack: 'y',
                  gap: 'xsmall',
                  paddingX: 'space.150',
                  paddingBottom: 'space.150',
                  paddingTop: 'xsmall',
                }}
              >
                <FormTextField
                  label={intl.formatMessage(messages.refundAddressLabel)}
                  placeholder={intl.formatMessage(
                    messages.refundAddressPlaceholder,
                  )}
                  value={customRefundAddress}
                  onChange={onRefundAddressChange}
                  error={addressError}
                />
                <Text
                  type="caption"
                  css={{color: tokens.color.neutral[900].toString()}}
                >
                  {intl.formatMessage(messages.refundNetworkLabel, {
                    networkName,
                  })}
                </Text>
              </view.div>
            </view.div>
          ) : null}
        </AnimatePresence>
      </view.div>
    </view.div>
  );
};
